import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Example, ExampleRatings, Entity, FilterMode, LabelDefinition } from '../types';
import EntitySpans from './EntitySpans';
import EntityTypeSelector from './EntityTypeSelector';
import AnnotationCard from './AnnotationCard';
import AddExampleSeparator from './AddExampleSeparator';
import DiffView from './DiffView';
import LabelConfusionMatrix from './LabelConfusionMatrix';
import { ratingToCategory } from '../utils/scoreUtils';
import { formatTime } from '../utils/inferenceUtils';

interface ComparisonViewProps {
  examples: Example[];
  modelNames: string[];
  scores: ExampleRatings;
  onScoreUpdate: (exampleId: string, modelName: string, score: any) => void;
  onAnnotationsUpdate: (exampleId: string, annotations: Entity[]) => void;
  allowAddExamples?: boolean;
  customLabelColors?: Record<string, string>;
  /** BQ-16: when 'disagreements' or 'errors-vs-gold', each example renders as
   *  a stacked DiffView (Gold + per-model rows) instead of the per-model card
   *  grid. 'all' and 'unscored' keep the original card grid. */
  filterMode?: FilterMode;
  /** BQ-15: promote a model prediction onto humanAnnotations. */
  onPromoteEntity?: (exampleId: string, modelName: string, entity: Entity) => void;
  /** BQ-15: reject a model prediction (excluded from FP / errors-vs-gold). */
  onRejectEntity?: (exampleId: string, modelName: string, entity: Entity) => void;
  /** BQ-18: per-label annotation guidelines. Threaded into EntitySpans + DiffView. */
  labelDefinitions?: Record<string, LabelDefinition>;
  /** BQ-20: cross-model label-confusion merge handler. Wired to App-level
   *  `handleMergeLabel`. When provided, the confusion-matrix panel renders a
   *  "Suggest merge" affordance on off-diagonal high-confidence cells. */
  onMergeLabels?: (from: string, to: string) => void;
}

// goldEdit serves two flows:
//   - mode: 'edit'   → user clicked an existing gold span; replace its label.
//   - mode: 'create' → user highlighted text in the gold row; append a new
//                      Entity at (start, end) with the chosen label.
type GoldEditState =
  | {
      mode: 'edit';
      exampleId: string;
      index: number;
      position: { top: number; left: number };
      initialValue: string;
    }
  | {
      mode: 'create';
      exampleId: string;
      selectedText: string;
      start: number;
      end: number;
      position: { top: number; left: number };
    };

const ComparisonView: React.FC<ComparisonViewProps> = ({
  examples,
  modelNames,
  scores,
  onScoreUpdate,
  onAnnotationsUpdate,
  allowAddExamples = false,
  customLabelColors,
  filterMode = 'all',
  onPromoteEntity,
  onRejectEntity,
  labelDefinitions,
  onMergeLabels,
}) => {
  // BQ-20: collapsible label-confusion-matrix panel above the example list.
  // Default collapsed so the existing example flow is unchanged on load.
  const [showConfusion, setShowConfusion] = useState<boolean>(false);
  // BQ-15: predicate factories — given an example + model row, produce
  // (entity → boolean) checkers that the read-only EntitySpans uses to apply
  // promoted/rejected styles.
  const makeIsPromoted = useCallback(
    (example: Example) => (entity: Entity) => {
      return (example.humanAnnotations ?? []).some(
        (e) =>
          e.start === entity.start &&
          e.end === entity.end &&
          e.label === entity.label
      );
    },
    []
  );
  const makeIsRejected = useCallback(
    (example: Example, modelName: string) => (entity: Entity) => {
      return (example.rejectedPredictions ?? []).some(
        (r) =>
          r.modelName === modelName &&
          r.entity.start === entity.start &&
          r.entity.end === entity.end &&
          r.entity.label === entity.label
      );
    },
    []
  );
  // Click-to-edit state for the editable gold-row <EntitySpans>.
  const [goldEdit, setGoldEdit] = useState<GoldEditState | null>(null);

  const isDiffMode = filterMode === 'disagreements' || filterMode === 'errors-vs-gold';

  // BQ-16: model-pair selector for diff modes. Only relevant when there are
  // more than two models — with two or fewer, all rows fit in the diff view
  // unambiguously. Default to the first two model names alphabetically.
  const sortedModelNames = useMemo(
    () => [...modelNames].sort((a, b) => a.localeCompare(b)),
    [modelNames]
  );
  const showPairSelector = isDiffMode && sortedModelNames.length > 2;
  const [pairA, setPairA] = useState<string>(sortedModelNames[0] ?? '');
  const [pairB, setPairB] = useState<string>(sortedModelNames[1] ?? '');

  // Re-seed the pair when the underlying model list changes (e.g., a new
  // file is loaded) so we don't keep a stale name that no longer exists.
  useEffect(() => {
    if (!sortedModelNames.includes(pairA)) {
      setPairA(sortedModelNames[0] ?? '');
    }
    if (!sortedModelNames.includes(pairB) || pairB === pairA) {
      const fallback = sortedModelNames.find((m) => m !== (sortedModelNames[0] ?? '')) ?? '';
      setPairB(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedModelNames.join('|')]);

  // Extract all unique entity types from all models
  const availableEntityTypes = useMemo(() => {
    const types = new Set<string>();
    examples.forEach(example => {
      example.modelResponses.forEach(response => {
        response.entities.forEach(entity => {
          types.add(entity.label);
        });
      });
      // Also include entity types from human annotations
      example.humanAnnotations?.forEach(entity => {
        types.add(entity.label);
      });
    });
    // Also include any custom labels that have been created but not yet used
    if (customLabelColors) {
      Object.keys(customLabelColors).forEach(label => {
        types.add(label);
      });
    }
    return Array.from(types).sort();
  }, [examples, customLabelColors]);

  const getModelResponse = (example: Example, modelName: string) => {
    return example.modelResponses.find(response => response.modelName === modelName);
  };

  const getExampleScore = (exampleId: string, modelName: string) => {
    return scores[exampleId]?.[modelName];
  };

  const getScoreColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    if (rating >= 2) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleGoldSpanClick = useCallback(
    (exampleId: string, entity: Entity, idx: number, event?: MouseEvent) => {
      // Best-effort popup positioning; fall back to a centered location.
      let top = 200;
      let left = 200;
      if (event && (event as any).clientX !== undefined) {
        top = (event as MouseEvent).clientY + 5;
        left = (event as MouseEvent).clientX;
      }
      setGoldEdit({
        mode: 'edit',
        exampleId,
        index: idx,
        position: { top, left },
        initialValue: entity.label
      });
    },
    []
  );

  // BQ-22 follow-up: restores text-selection-to-popup on the gold row.
  // BQ-14 deleted AnnotatedText.tsx, which had this flow; only click-on-
  // existing-entity got wired into <EntitySpans>. Here we open the
  // EntityTypeSelector in 'create' mode so the chosen label appends a
  // new Entity to humanAnnotations.
  const handleGoldTextSelect = useCallback(
    (exampleId: string, sel: { text: string; start: number; end: number; position: { top: number; left: number } }) => {
      setGoldEdit({
        mode: 'create',
        exampleId,
        selectedText: sel.text,
        start: sel.start,
        end: sel.end,
        position: sel.position
      });
    },
    []
  );

  const handleGoldEditSelect = useCallback(
    (entityType: string) => {
      if (!goldEdit) return;
      const example = examples.find(e => e.id === goldEdit.exampleId);
      if (!example) {
        setGoldEdit(null);
        return;
      }
      if (goldEdit.mode === 'edit') {
        const next = (example.humanAnnotations || []).map((e, i) =>
          i === goldEdit.index ? { ...e, label: entityType } : e
        );
        onAnnotationsUpdate(goldEdit.exampleId, next);
      } else {
        // create
        const newEntity: Entity = {
          text: goldEdit.selectedText,
          label: entityType,
          start: goldEdit.start,
          end: goldEdit.end
        };
        const next = [...(example.humanAnnotations || []), newEntity];
        onAnnotationsUpdate(goldEdit.exampleId, next);
      }
      setGoldEdit(null);
    },
    [goldEdit, examples, onAnnotationsUpdate]
  );

  return (
    <div className="space-y-6">
      {/* Top separator on background */}
      {
        <div className="px-4">
          <AddExampleSeparator
            disabled={!allowAddExamples}
            disabledTooltip="add new disabled - change in settings"
            onAdd={(text) => {
              const event = new CustomEvent('add-example', { detail: { text, insertAfterId: null } });
              window.dispatchEvent(event);
            }}
          />
        </div>
      }

      {/* BQ-20: collapsible label-confusion-matrix panel.
          Only meaningful when there's something to compare — at least one
          model row, since the matrix pairs an annotator on each side.
          Annotation-only projects (zero models) hide it entirely. */}
      {modelNames.length > 0 && (
        <div className="px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => setShowConfusion((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
              aria-expanded={showConfusion}
              aria-controls="label-confusion-panel"
              title="Cross-annotator confusion matrix: where two models (or a model vs your gold annotations) overlap on a span but disagree on the label. Surfaces 'Suggest merge' candidates for label normalization."
            >
              <span>Show label confusion</span>
              <svg
                className={`w-4 h-4 transition-transform ${showConfusion ? 'rotate-90' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {showConfusion && (
              <div id="label-confusion-panel" className="border-t border-gray-200 p-3">
                <LabelConfusionMatrix
                  examples={examples}
                  modelNames={modelNames}
                  customLabelColors={customLabelColors}
                  onMergeLabels={onMergeLabels}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* BQ-16: model-pair selector. Only visible in diff modes when there
          are more than two models — with two-or-fewer the diff view shows
          everything by default. */}
      {showPairSelector && (
        <div className="px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Compare:</span>
            <select
              value={pairA}
              onChange={(e) => setPairA(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Compare model A"
            >
              {sortedModelNames.map((m) => (
                <option key={`A-${m}`} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <span className="text-xs font-semibold text-gray-600">vs:</span>
            <select
              value={pairB}
              onChange={(e) => setPairB(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Compare model B"
            >
              {sortedModelNames.map((m) => (
                <option key={`B-${m}`} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {pairA === pairB && (
              <span className="text-xs text-amber-700">Pick two different models.</span>
            )}
          </div>
        </div>
      )}

      {examples.map((example, index) => {
        const exampleScores = scores[example.id] || {};
        const exampleNumber = index + 1;

        return (
          <div
            key={example.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200"
          >
            {/* Example Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-gray-500">#{exampleNumber}</span>
                    <h3 className="text-lg font-medium text-gray-900">
                      <EntitySpans
                        text={example.text}
                        entities={example.humanAnnotations || []}
                        readOnly={false}
                        onChange={(next) => onAnnotationsUpdate(example.id, next)}
                        customColors={customLabelColors}
                        labelDefinitions={labelDefinitions}
                        onEntityClick={(entity, idx) => {
                          handleGoldSpanClick(example.id, entity, idx);
                        }}
                        onSelectText={(sel) => handleGoldTextSelect(example.id, sel)}
                      />
                    </h3>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  {Object.entries(exampleScores).map(([modelName, score]) => (
                    <div
                      key={modelName}
                      className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(score.rating)}`}
                    >
                      {modelName}: {score.rating}/5
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* BQ-16: in diff modes the body becomes a stacked
                Gold + per-model DiffView and the per-model card grid is
                hidden. The example header above (with click-to-edit gold)
                stays so the user can still correct gold while triaging
                disagreements. */}
            {isDiffMode ? (
              <div className="p-4">
                <DiffView
                  example={example}
                  modelNames={modelNames}
                  selectedModelA={showPairSelector ? pairA : undefined}
                  selectedModelB={showPairSelector ? pairB : undefined}
                  customLabelColors={customLabelColors}
                  labelDefinitions={labelDefinitions}
                  onPromoteEntity={onPromoteEntity}
                  onRejectEntity={onRejectEntity}
                />
              </div>
            ) : (
            <>
            {/* Stacked annotators: Gold (handled in header above) +
                read-only model rows below. */}
            <div className="px-4 pt-3 space-y-2">
              {modelNames.map((modelName) => {
                const response = getModelResponse(example, modelName);
                if (!response) return null;
                return (
                  <div
                    key={`row-${modelName}`}
                    className="text-sm text-gray-800 bg-gray-50 rounded px-3 py-2 border border-gray-100"
                  >
                    <div className="text-xs font-semibold text-gray-500 mb-1">{modelName}</div>
                    <EntitySpans
                      text={example.text}
                      entities={response.entities}
                      readOnly
                      customColors={customLabelColors}
                      labelDefinitions={labelDefinitions}
                      onPromote={
                        onPromoteEntity
                          ? (entity) => onPromoteEntity(example.id, modelName, entity)
                          : undefined
                      }
                      onReject={
                        onRejectEntity
                          ? (entity) => onRejectEntity(example.id, modelName, entity)
                          : undefined
                      }
                      isPromoted={makeIsPromoted(example)}
                      isRejected={makeIsRejected(example, modelName)}
                    />
                  </div>
                );
              })}
            </div>

            {/* Model Responses */}
            <div className="p-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                {/* Human Annotation Card (expand to fill available width) */}
                <div className="col-span-full">
                  <AnnotationCard
                    exampleText={example.text}
                    annotations={example.humanAnnotations || []}
                    availableEntityTypes={availableEntityTypes}
                    onAnnotationsChange={(annotations) => onAnnotationsUpdate(example.id, annotations)}
                    customLabelColors={customLabelColors}
                  />
                </div>

                {/* Model Cards */}
                {modelNames.map((modelName) => {
                  const response = getModelResponse(example, modelName);
                  const score = getExampleScore(example.id, modelName) || {
                    rating: 0,
                    category: 'Fair' as const,
                    notes: ''
                  };

                  const derivedCategory = score.rating > 0 ? ratingToCategory(score.rating) : null;
                  return (
                    <div
                      key={modelName}
                      className={`p-3 rounded-lg border overflow-hidden ${
                        derivedCategory === 'Best' ? 'bg-green-50 border-green-200' :
                        derivedCategory === 'Good' ? 'bg-blue-50 border-blue-200' :
                        derivedCategory === 'Fair' ? 'bg-yellow-50 border-yellow-200' :
                        derivedCategory === 'Poor' ? 'bg-red-50 border-red-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Model Header */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{modelName}</h4>
                          {score.rating > 0 && (
                            <div className={`text-sm font-medium ${getScoreColor(score.rating)}`}>
                              {score.rating}/5
                            </div>
                          )}
                        </div>
                        {response?.inferenceTime !== undefined && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatTime(response.inferenceTime)}
                          </div>
                        )}
                      </div>

                      {/* Entities (inline span view per model, read-only) */}
                      <div className="mb-3">
                        {response ? (
                          <EntitySpans
                            text={example.text}
                            entities={response.entities}
                            readOnly
                            customColors={customLabelColors}
                            labelDefinitions={labelDefinitions}
                            onPromote={
                              onPromoteEntity
                                ? (entity) => onPromoteEntity(example.id, modelName, entity)
                                : undefined
                            }
                            onReject={
                              onRejectEntity
                                ? (entity) => onRejectEntity(example.id, modelName, entity)
                                : undefined
                            }
                            isPromoted={makeIsPromoted(example)}
                            isRejected={makeIsRejected(example, modelName)}
                          />
                        ) : (
                          <p className="text-gray-500 text-sm">No response found</p>
                        )}
                      </div>

                      {/* Inline Scoring */}
                      <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                        {/* Star Rating */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">Rating:</span>
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                key={rating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onScoreUpdate(example.id, modelName, { ...score, rating });
                                }}
                                className={`w-6 h-6 rounded-full text-xs font-medium transition-colors ${
                                  score.rating === rating
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                title={`Rate ${rating} out of 5`}
                              >
                                {rating}
                              </button>
                            ))}
                          </div>
                        </div>

                      {/* Category removed: derived from rating */}

                        {/* Notes */}
                        <div>
                          <textarea
                            value={score.notes}
                            onChange={(e) => {
                              e.stopPropagation();
                              onScoreUpdate(example.id, modelName, { ...score, notes: e.target.value });
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Add notes..."
                            className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </>
            )}
          </div>
        );
      })}

      {/* Bottom separator on background (after last card) */}
      {examples.length > 0 && (
        <div className="px-4">
          <AddExampleSeparator
            disabled={!allowAddExamples}
            disabledTooltip="add new disabled - change in settings"
            onAdd={(text) => {
              const lastId = examples[examples.length - 1].id;
              const event = new CustomEvent('add-example', { detail: { text, insertAfterId: lastId } });
              window.dispatchEvent(event);
            }}
          />
        </div>
      )}

      {examples.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No examples found. Try adjusting your search terms.</p>
        </div>
      )}

      {/* Gold-row label-pick popup. Two flows share the selector:
          - 'edit'   → user clicked an existing gold span; replaces label.
          - 'create' → user highlighted text; appends new humanAnnotation. */}
      {goldEdit && (
        <EntityTypeSelector
          availableTypes={availableEntityTypes}
          onSelect={handleGoldEditSelect}
          onCancel={() => setGoldEdit(null)}
          position={goldEdit.position}
          initialValue={goldEdit.mode === 'edit' ? goldEdit.initialValue : ''}
        />
      )}
    </div>
  );
};

export default ComparisonView;
